import * as React from "react";
import * as ReactDom from "react-dom";
import {
  MoveFn,
  DragListContext,
  DragListItemContext,
  isDragListItemData,
} from "./utils";
import { Slot } from "@radix-ui/react-slot";
import { FormattedMessage } from "react-intl";

import {
  draggable,
  dropTargetForElements,
  monitorForElements,
  type ElementDropTargetEventBasePayload,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";

import {
  extractClosestEdge,
  attachClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { getReorderDestinationIndex } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index";

import { Button } from "@/kit/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/kit/ui/dropdown-menu";
import { DotsGridIcon } from "@/icons";

export interface DragListProps {
  move: MoveFn;
  length: number;
}

export const DragList = ({
  move: moveIndex,
  length,
  children,
}: React.PropsWithChildren<DragListProps>) => {
  const id = React.useId();
  const [lastMoved, setLastMoved] = React.useState<number | undefined>(
    undefined,
  );

  const move: MoveFn = React.useCallback(
    (from, to) => {
      moveIndex(from, to);
      setLastMoved(to);
    },
    [moveIndex, setLastMoved],
  );

  React.useEffect(() => {
    return monitorForElements({
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }
        const sourceData = source.data;
        const targetData = target.data;
        if (
          !isDragListItemData(sourceData) ||
          !isDragListItemData(targetData)
        ) {
          return;
        }
        if (sourceData.listId !== id || targetData.listId !== id) {
          return;
        }
        const finishIndex = getReorderDestinationIndex({
          startIndex: sourceData.index,
          closestEdgeOfTarget: extractClosestEdge(targetData),
          indexOfTarget: targetData.index,
          axis: "vertical",
        });
        if (sourceData.index != finishIndex) {
          move(sourceData.index, finishIndex);
        }
      },
    });
  }, [move, id]);

  return (
    <DragListContext.Provider value={{ id, move, lastMoved, length }}>
      {children}
    </DragListContext.Provider>
  );
};

export interface DragListItemProps {
  index: number;
}

export const DragListItem = ({
  index,
  children,
}: React.PropsWithChildren<DragListItemProps>) => {
  const { id: listId } = React.useContext(DragListContext);
  const ref = React.useRef<HTMLElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOverEdge, setDragOverEdge] = React.useState<
    "top" | "bottom" | null
  >(null);
  const data = React.useMemo(() => ({ index, listId }), [index, listId]);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const onDragOver = ({
      source,
      self,
    }: ElementDropTargetEventBasePayload) => {
      const sourceData = source.data;
      if (!isDragListItemData(sourceData)) {
        return;
      }
      const sourceIndex = sourceData.index;

      const isSource = data.index === sourceIndex;
      if (isSource) {
        setDragOverEdge(null);
        return;
      }

      const closestEdge = extractClosestEdge(self.data);
      if (closestEdge !== "top" && closestEdge !== "bottom") {
        return;
      }

      const isItemBeforeSource = data.index === sourceIndex - 1;
      const isItemAfterSource = data.index === sourceIndex + 1;

      const isDropIndicatorHidden =
        (isItemBeforeSource && closestEdge === "bottom") ||
        (isItemAfterSource && closestEdge === "top");

      if (isDropIndicatorHidden) {
        setDragOverEdge(null);
        return;
      }

      setDragOverEdge(closestEdge);
    };

    return dropTargetForElements({
      element,
      canDrop({ source }) {
        return (
          isDragListItemData(source.data) && source.data.listId === data.listId
        );
      },
      getData({ input }) {
        return attachClosestEdge(data, {
          element,
          input,
          allowedEdges: ["top", "bottom"],
        });
      },
      onDragEnter: onDragOver,
      onDrag: onDragOver,
      onDragLeave() {
        setDragOverEdge(null);
      },
      onDrop() {
        setDragOverEdge(null);
      },
    });
  }, [data]);

  return (
    <DragListItemContext.Provider
      value={{ data, dragOverEdge, isDragging, setIsDragging }}
    >
      <Slot ref={ref}>{children}</Slot>
    </DragListItemContext.Provider>
  );
};

export interface DragHandleProps {
  preview?: React.ReactNode;
  previewOffset?: { x: string; y: string };
}

export const DragHandle = ({
  preview,
  previewOffset = { x: "0px", y: "0px" },
  children,
}: React.PropsWithChildren<DragHandleProps>) => {
  const { data, setIsDragging } = React.useContext(DragListItemContext);
  const ref = React.useRef<HTMLElement>(null);
  const [previewContainer, setPreviewContainer] =
    React.useState<HTMLElement | null>(null);
  const hasCustomPreview = preview !== undefined;

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    return draggable({
      element: element,
      getInitialData: () => data,
      ...(hasCustomPreview && {
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview(previewOffset),
            render({ container }) {
              setPreviewContainer(container);
              return () => setPreviewContainer(null);
            },
          });
        },
      }),
      onDragStart() {
        setIsDragging(true);
      },
      onDrop() {
        setIsDragging(false);
      },
    });
  }, [data, setIsDragging, previewOffset, hasCustomPreview]);
  return (
    <>
      <Slot ref={ref}>{children}</Slot>
      {previewContainer && ReactDom.createPortal(preview, previewContainer)}
    </>
  );
};

export const DragListHandle = ({ ...dragHandleProps }: DragHandleProps) => {
  const { move, length } = React.useContext(DragListContext);
  const { data } = React.useContext(DragListItemContext);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const canMoveUp = data.index > 0;
  const canMoveDown = data.index < length - 1;
  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger className="relative -z-10" />
      <DragHandle {...dragHandleProps}>
        <Button
          variant="tertiary"
          icon
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDropdownOpen(true);
          }}
        >
          <DotsGridIcon />
        </Button>
      </DragHandle>
      <DropdownMenuContent>
        <DropdownMenuItem
          disabled={!canMoveUp}
          className="hover:cursor-pointer"
          onClick={() => move(data.index, 0)}
        >
          <FormattedMessage defaultMessage="Move to top" />
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canMoveUp}
          className="hover:cursor-pointer"
          onClick={() => move(data.index, data.index - 1)}
        >
          <FormattedMessage defaultMessage="Move up" />
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canMoveDown}
          className="hover:cursor-pointer"
          onClick={() => move(data.index, data.index + 1)}
        >
          <FormattedMessage defaultMessage="Move down" />
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canMoveDown}
          className="hover:cursor-pointer"
          onClick={() => move(data.index, length - 1)}
        >
          <FormattedMessage defaultMessage="Move to bottom" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
